import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";

// Added Icons for Quick Actions
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SensorsIcon from "@mui/icons-material/Sensors";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { useNavigate } from "react-router-dom";
import { T } from "./VoiceCallLogsTable";
import {
  VoiceCardStyle,
  VoiceReportStatCard,
  VoiceTooltipStyle,
  fmtDuration,
} from "./VoiceFullReports";
import useApiEp from "../../hooks/useApiEp";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
} from "recharts";

const VoiceCallsDashboard = () => {
  const navigate = useNavigate(); // Wired up the router

  const { data: stats = {}, isLoading: sLoad } = useApiEp(
    `/voice/get-voice-report-stats`
  );

  const pieData = [
    { name: "Answered", value: stats.answeredCalls || 0, color: T.green },
    { name: "Not Answered", value: stats.notAnsweredCalls || 0, color: T.red },
  ];

  const statCards = [
    {
      label: "Total Calls",
      value: stats.totalCalls,
      icon: <PhoneIcon sx={{ fontSize: 20 }} />,
      color: T.blue,
      bg: T.blueBg,
      border: T.blueBorder,
    },
    {
      label: "Answered",
      value: stats.answeredCalls,
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />,
      color: T.green,
      bg: T.greenBg,
      border: T.greenBorder,
    },
    {
      label: "Not Answered",
      value: stats.notAnsweredCalls,
      icon: <HighlightOffIcon sx={{ fontSize: 20 }} />,
      color: T.red,
      bg: T.redBg,
      border: T.redBorder,
    },
    {
      label: "Answer Rate",
      value: stats.answerRate != null ? `${stats.answerRate}%` : "—",
      icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
      color: T.purple,
      bg: T.purpleBg,
      border: T.purpleBorder,
    },
    {
      label: "Avg Duration",
      value: fmtDuration(stats.avgDuration),
      icon: <TimerOutlinedIcon sx={{ fontSize: 20 }} />,
      color: T.amber,
      bg: T.amberBg,
      border: T.amberBorder,
    },
  ];

  // Define Quick Actions
  const quickActions = [
    {
      label: "Launch Campaign",
      icon: <RocketLaunchIcon fontSize="small" />,
      path: "/home/launch-voice-campaign",
    },
    {
      label: "Live Campaign",
      icon: <SensorsIcon fontSize="small" />,
      path: "/home/live-voice-report",
    },
    {
      label: "Voice Files",
      icon: <AudioFileIcon fontSize="small" />,
      path: "/home/voice-files",
    },
    {
      label: "Manage Agents",
      icon: <SupportAgentIcon fontSize="small" />,
      path: "/home/agents-list",
    },
    {
      label: "Main Report",
      icon: <AssessmentIcon fontSize="small" />,
      path: "/home/voice-reports-view",
    },
    {
      label: "Transactions",
      icon: <ReceiptLongIcon fontSize="small" />,
      path: "/home/voice-history",
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Voice Calls Dashboard</Typography>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Real-time data
      </Typography>

      {/* Quick Actions Bar */}
      {/* Quick Actions Bar */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: 1.25,
          mb: 3,
        }}
      >
        {quickActions.map((action, index) => {
          const iconColors = [
            { bg: "#EEEDFE", color: "#534AB7" },
            { bg: "#E1F5EE", color: "#0F6E56" },
            { bg: "#E6F1FB", color: "#185FA5" },
            { bg: "#FAEEDA", color: "#854F0B" },
            { bg: "#FAECE7", color: "#993C1D" },
            { bg: "#EAF3DE", color: "#3B6D11" },
          ];
          const ic = iconColors[index % iconColors.length];

          return (
            <Box
              key={index}
              onClick={() => navigate(action.path)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                p: "14px 10px 12px",
                borderRadius: 3,
                border: "0.5px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                cursor: "pointer",
                textAlign: "center",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: "grey.400",
                  backgroundColor: "action.hover",
                  transform: "translateY(-1px)",
                },
                "&:active": { transform: "scale(0.97)" },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: ic.bg,
                  color: ic.color,
                  flexShrink: 0,
                }}
              >
                {React.cloneElement(action.icon, { sx: { fontSize: 22 } })}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: "text.primary",
                  }}
                >
                  {action.label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                  {action.sublabel}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((s) => (
          <Grid item xs={6} sm={4} md={12 / 5} key={s.label}>
            <VoiceReportStatCard {...s} loading={sLoad} />
          </Grid>
        ))}
      </Grid>

      {/* Charts Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Pie Chart */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ ...VoiceCardStyle, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 0.5,
                }}
              >
                Answer Rate
              </Typography>
              <Box sx={{ flex: 1, position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="46%"
                      innerRadius={58}
                      outerRadius={82}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {pieData.map((e) => (
                        <Cell key={e.name} fill={e.color} />
                      ))}
                    </Pie>
                    <RTooltip {...VoiceTooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute",
                    top: "40%",
                    left: "50%",
                    transform: "translate(-50%,-50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: T.text,
                      lineHeight: 1,
                    }}
                  >
                    {stats.answerRate ?? 0}%
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: T.muted,
                      mt: 0.3,
                      fontWeight: 500,
                    }}
                  >
                    answer rate
                  </Typography>
                </Box>
              </Box>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 0.5 }}
              >
                {pieData.map((e) => (
                  <Box
                    key={e.name}
                    sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: e.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 11, color: T.muted, fontWeight: 500 }}
                    >
                      {e.name}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Line Chart */}
        <Grid item xs={12} md={5}>
          <Card sx={{ ...VoiceCardStyle, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 1,
                }}
              >
                Daily Volume
              </Typography>
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={stats.byDate || []}
                    margin={{ top: 4, right: 16, left: -24, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.borderLight}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: T.subtle, fontSize: 10 }}
                      tickFormatter={(d) => d?.slice(5)}
                    />
                    <YAxis tick={{ fill: T.subtle, fontSize: 10 }} />
                    <RTooltip {...VoiceTooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11, color: T.muted }} />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke={T.blue}
                      strokeWidth={2.5}
                      dot={false}
                      name="Total"
                    />
                    <Line
                      type="monotone"
                      dataKey="answered"
                      stroke={T.green}
                      strokeWidth={2.5}
                      dot={false}
                      name="Answered"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Bar Chart */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ ...VoiceCardStyle, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 1,
                }}
              >
                DTMF Keys Pressed
              </Typography>
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.byDtmf || []}
                    margin={{ top: 4, right: 8, left: -24, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.borderLight}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="dtmf"
                      tick={{
                        fill: T.textSecondary,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    />
                    <YAxis tick={{ fill: T.subtle, fontSize: 10 }} />
                    <RTooltip {...VoiceTooltipStyle} />
                    <Bar
                      dataKey="count"
                      fill={T.purple}
                      radius={[4, 4, 0, 0]}
                      name="Calls"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VoiceCallsDashboard;
